import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import startOfDay from 'date-fns/startOfDay';

import FormGroup from '../../../../../../../components/global/FormGroup';
import Alert from '../../../../../../../components/global/Alert';
import { getFormikTouchedErrors } from '../../../../../../../util/formik';
import Label from '../../../../../../../components/global/Label';
import DatePickerWithYearDropdown from '../../../../../../../components/global/DatePickerWithYearDropdown';
import InputGroup from '../../../../../../../components/global/InputGroup';
import { getAsset } from '../../../../../../../util/assets';
import { EVENT_DETAILS_CONTEXT } from '../utils';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    email_address: PropTypes.string,
  }),
  values: PropTypes.shape({
    name: PropTypes.string,
    anniversary_type: PropTypes.string,
    date_of_birth: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    date_of_passing: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  touched: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  context: PropTypes.oneOf(Object.values(EVENT_DETAILS_CONTEXT)),
};

const MemorialEventDetails = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  context,
}) => {
  const dateInputRef = useRef();
  const dateOfBirthInputRef = useRef();
  const dateOfPassingInputRef = useRef();
  const errorList = Object.values(getFormikTouchedErrors(errors, touched));
  const {
    date_of_birth: dateOfBirth,
    date_of_passing: dateOfPassing,
    date: eventDate,
  } = values;

  useEffect(() => {
    if (dateInputRef?.current) {
      dateInputRef.current.readOnly = 'readonly';
    }
  }, [dateInputRef]);

  useEffect(() => {
    if (dateOfBirthInputRef?.current) {
      dateOfBirthInputRef.current.readOnly = 'readonly';
    }
  }, [dateOfBirthInputRef]);

  useEffect(() => {
    if (dateOfPassingInputRef?.current) {
      dateOfPassingInputRef.current.readOnly = 'readonly';
    }
  }, [dateOfPassingInputRef]);

  return (
    <Fragment>
      <div className="row">
        <div className="col-12">
          <FormGroup
            id="name"
            name="name"
            error={!!errors.name}
            label={
              context === EVENT_DETAILS_CONTEXT.SLIDESHOW
                ? 'Slideshow Name'
                : 'Event Name'
            }
            placeholder="Enter event name here..."
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-lg-6 col-12">
          <FormGroup
            id="first_name"
            name="first_name"
            error={!!errors.first_name}
            label="Name of deceased"
            placeholder="First name"
            className="mb-0 mb-lg-2"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.first_name}
          />
        </div>
        <div className="col-lg-6 col-12">
          <FormGroup
            id="last_name"
            name="last_name"
            error={!!errors.last_name}
            label={<div className="d-none d-lg-block">&nbsp;</div>}
            labelProps={{ style: { opacity: 0 } }}
            placeholder="Last name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.last_name}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-lg-6 col-12">
          <div className="form-group">
            <Label id="date_of_birth" optional>
              Deceased date of birth
            </Label>
            <div className="datepicker-gray-light">
              <DatePickerWithYearDropdown
                id="date_of_birth"
                name="date_of_birth"
                customInput={
                  <InputGroup
                    id="date_of_birth"
                    name="date_of_birth"
                    error={!!errors.date_of_birth}
                    type="text"
                    autoComplete="off"
                    inputRef={dateOfBirthInputRef}
                    addon={
                      <img
                        alt=""
                        src={getAsset('/shutterbug/icons/calendar.svg')}
                      />
                    }
                  />
                }
                popperClassName="freemium-event-date-picker-popper"
                wrapperClassName="freemium-event-date-picker"
                onChange={selectedDate => {
                  handleChange({
                    target: {
                      name: 'date_of_birth',
                      value: startOfDay(selectedDate),
                    },
                  });
                  if (startOfDay(selectedDate) > dateOfPassing) {
                    handleChange({
                      target: {
                        name: 'date_of_passing',
                        value: null,
                      },
                    });
                  }
                }}
                selected={dateOfBirth}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-12">
          <div className="form-group">
            <Label id="date_of_passing" optional>
              Deceased date of passing
            </Label>
            <div className="datepicker-gray-light">
              <DatePickerWithYearDropdown
                id="date_of_passing"
                name="date_of_passing"
                customInput={
                  <InputGroup
                    id="date_of_passing"
                    name="date_of_passing"
                    error={!!errors.date_of_passing}
                    type="text"
                    autoComplete="off"
                    inputRef={dateOfPassingInputRef}
                    addon={
                      <img
                        alt=""
                        src={getAsset('/shutterbug/icons/calendar.svg')}
                      />
                    }
                  />
                }
                popperClassName="freemium-event-date-picker-popper"
                wrapperClassName="freemium-event-date-picker"
                onChange={selectedDate => {
                  handleChange({
                    target: {
                      name: 'date_of_passing',
                      value: startOfDay(selectedDate),
                    },
                  });
                }}
                selected={dateOfPassing}
                minDate={dateOfBirth}
              />
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="form-group">
            <Label id="event_date" optional>
              Memorial date
            </Label>
            <div className="datepicker-gray-light">
              <DatePickerWithYearDropdown
                id="date"
                name="date"
                customInput={
                  <InputGroup
                    id="date"
                    name="date"
                    error={!!errors.date}
                    type="text"
                    autoComplete="off"
                    inputRef={dateInputRef}
                    addon={
                      <img
                        alt=""
                        src={getAsset('/shutterbug/icons/calendar.svg')}
                      />
                    }
                  />
                }
                popperClassName="freemium-event-date-picker-popper"
                wrapperClassName="freemium-event-date-picker"
                onChange={selectedDate => {
                  handleChange({
                    target: {
                      name: 'date',
                      value: startOfDay(selectedDate),
                    },
                  });
                }}
                selected={eventDate}
              />
            </div>
          </div>
        </div>
      </div>

      {errorList.length > 0 && (
        <Alert type="danger" className="mt-1 text-normal">
          <ul className="mb-0">
            {errorList.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Fragment>
  );
};

MemorialEventDetails.propTypes = propTypes;

export default MemorialEventDetails;
